import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Fehlerbuch-Erinnerung (Ralph 23.10.2026: „bei jedem Start als Erinnerung, kurz, ein Fenster, das wieder verschwindet“)
 * Einmal pro Sitzung nach dem Start: Stand von „Notizen & Bugs“ (Butler-Fehlerakten) und die zwei Regeln,
 * damit das Fehlerbuch unabhängig vom Meister-Chat gepflegt bleibt (CLAUDE.md, Erwartungen 12).
 * Verschwindet nach DAUER_MS von selbst; bleibt stehen, solange die Maus darauf liegt. Klick öffnet die Liste.
 * Nur für Admins (der Server liefert die Wiedervorlage nur ihnen); sonst wird nichts gezeigt.
 */
const SESSION_KEY = "graupner_fehlerbuch_erinnerung";
const DAUER_MS = 12000;

const FehlerbuchErinnerung = () => {
  const navigate = useNavigate();
  const [stand, setStand] = useState(null);
  const [halten, setHalten] = useState(false);

  useEffect(() => {
    try { if (sessionStorage.getItem(SESSION_KEY) === "1") return; } catch { /* ohne Speicher: trotzdem zeigen */ }
    if (!localStorage.getItem("token")) return;
    api.get("/butler/wiedervorlage")
      .then((r) => {
        setStand({ offen: r.data?.nicht_erledigt ?? 0, faellig: (r.data?.faellig || []).length });
        try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* egal */ }
      })
      .catch(() => {});  // kein Admin oder Server nicht erreichbar: nichts zeigen
  }, []);

  useEffect(() => {
    if (!stand || halten) return undefined;
    const t = setTimeout(() => setStand(null), DAUER_MS);
    return () => clearTimeout(t);
  }, [stand, halten]);

  if (!stand) return null;
  const oeffnen = () => { setStand(null); navigate("/settings?tab=notizen-bugs"); };

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 top-16 lg:top-6 z-50 w-[92vw] max-w-md rounded-sm border border-amber-300 bg-amber-50 text-amber-950 shadow-lg overflow-hidden"
      onMouseEnter={() => setHalten(true)} onMouseLeave={() => setHalten(false)}
      role="status" data-testid="fehlerbuch-erinnerung"
    >
      <div className="flex items-start gap-3 p-3 cursor-pointer" onClick={oeffnen}>
        <ClipboardList className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div className="text-sm space-y-1">
          <p className="font-semibold" data-testid="fehlerbuch-erinnerung-stand">
            Fehlerbuch: {stand.offen} nicht erledigt{stand.faellig > 0 ? ` · ${stand.faellig} Wiedervorlage fällig` : ""}
          </p>
          <p>Fehler und Wünsche immer in <b>Notizen &amp; Bugs</b> – auch wenn Sie sie im Chat melden.</p>
          <p>Neuer Meister? Er muss zuerst nach der Fehlerliste fragen. Nach jedem Einspielen: Fehlerbuch-Abgleich.</p>
        </div>
        <button
          type="button" onClick={(e) => { e.stopPropagation(); setStand(null); }}
          className="ml-auto p-1 rounded-sm hover:bg-black/5" title="Schließen" aria-label="Schließen"
          data-testid="fehlerbuch-erinnerung-schliessen"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="h-1 bg-amber-200">
        <div className="h-1 bg-amber-500" style={{ width: "100%", animation: halten ? "none" : `fehlerbuchAblauf ${DAUER_MS}ms linear forwards` }} />
      </div>
      <style>{"@keyframes fehlerbuchAblauf { from { width: 100%; } to { width: 0%; } }"}</style>
    </div>
  );
};

export default FehlerbuchErinnerung;
